import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  MailIcon,
  PhoneIcon,
} from '@heroicons/react/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/solid';
import { GiftChoiceUnitData } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import {
  giftCouponByCodeDataAtomFamily,
  giftCouponByCodeStatusAtomFamily,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import {
  requestCouponRedemption,
  viewCoupon,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import { useGiftCouponByCodeHook } from '@shared/api/giftCoupon/giftCoupon.hook';
import {
  GiftCouponRedeemStatus,
  GiftCouponRequestDto,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { Loading } from '@shared/components/Loading';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useParams } from 'react-router-dom';

import { giftStoreNavBarTitleAtom } from '../containers/GiftStoreNavBar/giftStoreNavBar.atom';

// 교환 상태별 메시지
const getRedeemStatusMessage = (status: GiftCouponRedeemStatus) => {
  switch (status) {
    case GiftCouponRedeemStatus.Available:
      return null;
    case GiftCouponRedeemStatus.Requested:
      return {
        type: 'info',
        title: '신청이 완료되었습니다',
        message:
          '입력하신 번호로 곧 전송됩니다.\n잠시만 기다려 주세요.\n\n영업일 기준 10시, 14시, 18시에 발송됩니다.\n발송까지 30분~1시간 소요될 수 있습니다.\n(주말 및 공휴일 제외, 익영업일 기준)',
      };
    case GiftCouponRedeemStatus.Delivered:
      return {
        type: 'success',
        title: '전달이 완료되었습니다',
        message: '받으신 문자를 확인해 주세요.',
      };
    case GiftCouponRedeemStatus.Expired:
      return {
        type: 'error',
        title: '유효기간 만료',
        message: '이 답례품은 유효기간이 지났습니다.',
      };
    case GiftCouponRedeemStatus.Refunded:
      return {
        type: 'error',
        title: '환불 완료',
        message: '이 답례품은 환불 처리되었습니다.',
        hideContact: true,
      };
    case GiftCouponRedeemStatus.Canceled:
      return {
        type: 'error',
        title: '취소됨',
        message: '이 답례품은 취소되었습니다.',
        hideContact: true,
      };
    case GiftCouponRedeemStatus.Disposed:
      return {
        type: 'info',
        title: '이 쿠폰은 사용불가 처리되었습니다',
        message: '이 쿠폰은 더 이상 사용할 수 없습니다.\n불편을 드려 죄송합니다.',
        hideContact: true,
      };
    default:
      return null;
  }
};

// 전화번호 포맷팅
const formatPhoneNumber = (value: string) => {
  const numbers = value.replace(/[^0-9]/g, '');
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
};

const GiftCouponRedeemPage: FC = function GiftCouponRedeemPage() {
  const { couponCode } = useParams<{ couponCode: string }>();

  const setTitle = useSetRecoilState(giftStoreNavBarTitleAtom);

  // 폼 상태
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedChoiceUnit, setSelectedChoiceUnit] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // 쿠폰 조회
  useGiftCouponByCodeHook(couponCode ?? '');
  const status = useRecoilValue(giftCouponByCodeStatusAtomFamily(couponCode ?? ''));
  const coupon = useRecoilValue(giftCouponByCodeDataAtomFamily(couponCode ?? ''));

  useEffect(() => {
    setTitle('답례품 수령');
  }, [setTitle]);

  // 페이지 접속 시 view 처리 (id로 호출)
  useEffect(() => {
    if (coupon) {
      viewCoupon(coupon._id).catch(console.error);
    }
  }, [coupon]);

  // 상품 정보
  const giftItem = coupon?.giftItem;
  const giftItemInfo = typeof giftItem === 'object' ? giftItem?.giftItemInfo : null;

  // 선택형 상품 여부
  const isSelectable = giftItemInfo?.isSelectable ?? false;
  const choiceUnits = useMemo(() => {
    return (giftItemInfo?.choiceUnits ?? []) as GiftChoiceUnitData[];
  }, [giftItemInfo]);

  // 선택된 유닛 객체
  const selectedUnit = useMemo(() => {
    if (!selectedChoiceUnit) return null;
    return choiceUnits.find((u) => u._id === selectedChoiceUnit) ?? null;
  }, [selectedChoiceUnit, choiceUnits]);

  // 이미지 URL (선택된 유닛이 있으면 해당 이미지, 없으면 기본 이미지)
  const imageUrl = useMemo(() => {
    if (selectedUnit?.image?.url) {
      return selectedUnit.image.url;
    }
    if (typeof giftItem === 'object') {
      return giftItemInfo?.image?.url || giftItem?.image?.url || '';
    }
    return '';
  }, [giftItem, giftItemInfo, selectedUnit]);

  // 브랜드 이름 (선택된 유닛이 있으면 해당 브랜드, 없으면 기본 브랜드)
  const brandName = useMemo(() => {
    if (selectedUnit?.giftItemBrand && typeof selectedUnit.giftItemBrand === 'object') {
      return selectedUnit.giftItemBrand.name;
    }
    if (giftItemInfo) {
      const brand = giftItemInfo.giftItemBrand;
      if (brand && typeof brand === 'object' && 'name' in brand) {
        return brand.name;
      }
    }
    return null;
  }, [giftItemInfo, selectedUnit]);

  // 상품명
  const itemName =
    typeof giftItem === 'object' ? giftItem?.name || giftItemInfo?.name : '';

  // 쿠폰 상세 정보 (선택된 유닛이 있으면 해당 쿠폰 상세, 없으면 기본)

  const couponDetail = useMemo(() => {
    if (selectedUnit?.couponDetail) {
      return selectedUnit.couponDetail;
    }
    const brandCouponDetail =
      selectedUnit?.giftItemBrand && typeof selectedUnit.giftItemBrand === 'object'
        ? selectedUnit.giftItemBrand.couponDetail
        : undefined;
    return brandCouponDetail ?? giftItemInfo?.couponDetail;
  }, [giftItemInfo, selectedUnit]);

  const phoneInputRef = useRef<HTMLDivElement>(null);

  // 캐로셀 상태 (선택형 상품용)
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // 메인 이미지 URL (선택과 무관한 기본 이미지)
  const mainImageUrl = useMemo(() => {
    if (typeof giftItem === 'object') {
      return giftItemInfo?.image?.url || giftItem?.image?.url || '';
    }
    return '';
  }, [giftItem, giftItemInfo]);

  const carouselImages = useMemo(() => {
    if (!isSelectable || choiceUnits.length === 0) {
      return [
        {
          url: mainImageUrl,
          name: itemName || '답례품',
          unitId: null as string | null,
          brandName: brandName,
        },
      ];
    }
    const mainSlide = {
      url: mainImageUrl,
      name: itemName || '답례품',
      unitId: null as string | null,
      brandName: brandName,
    };
    const unitSlides = choiceUnits.map((unit) => ({
      url: unit.image?.url || '',
      name: unit.name,
      unitId: unit._id as string | null,
      brandName:
        unit.giftItemBrand && typeof unit.giftItemBrand === 'object'
          ? unit.giftItemBrand.name
          : null,
    }));
    return [mainSlide, ...unitSlides];
  }, [isSelectable, choiceUnits, mainImageUrl, itemName, brandName]);

  // 캐로셀 인덱스 변경 시 선택형이면 자동 선택 (index 0 = 메인 = 선택 없음)
  useEffect(() => {
    if (!isSelectable) return;
    const unitId = carouselImages[carouselIndex]?.unitId ?? null;
    setSelectedChoiceUnit(unitId);
  }, [carouselIndex, isSelectable, carouselImages]);

  // 하단 리스트에서 선택 시 캐로셀도 이동 + 번호 입력으로 자동 스크롤
  const handleSelectUnit = useCallback(
    (unitId: string | null) => {
      setSelectedChoiceUnit(unitId);
      if (unitId === null) {
        setCarouselIndex(0);
      } else {
        const idx = carouselImages.findIndex((item) => item.unitId === unitId);
        if (idx >= 0) setCarouselIndex(idx);

        setTimeout(() => {
          phoneInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
      }
    },
    [carouselImages],
  );

  const minSwipeDistance = 50;

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0]?.clientX ?? null);
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0]?.clientX ?? null);
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) {
      setCarouselIndex((prev) => (prev === carouselImages.length - 1 ? prev : prev + 1));
    } else if (distance < -minSwipeDistance) {
      setCarouselIndex((prev) => (prev === 0 ? prev : prev - 1));
    }
  }, [touchStart, touchEnd, carouselImages.length]);

  // 전화번호 입력 핸들러
  const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  }, []);

  // 교환 신청 핸들러 (id로 호출)
  const handleSubmit = useCallback(async () => {
    const cleanPhone = phoneNumber.replace(/-/g, '');

    if (!coupon || !cleanPhone) {
      setSubmitError('휴대폰 번호를 입력해 주세요.');
      return;
    }

    if (!/^01[016789]\d{8}$/.test(cleanPhone)) {
      setSubmitError('옳바른 휴대폰 번호를 입력해 주세요.');
      return;
    }

    if (isSelectable && !selectedChoiceUnit) {
      setSubmitError('교환할 상품을 선택해 주세요.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const dto: GiftCouponRequestDto = {
        name: '', // 핸드폰 번호만 필수
        phoneNumber: cleanPhone,
      };

      if (selectedChoiceUnit) {
        dto.selectedChoiceUnit = selectedChoiceUnit;
      }

      await requestCouponRedemption(coupon._id, dto);
      setSubmitSuccess(true);
    } catch (error) {
      console.error('교환 신청 실패:', error);
      setSubmitError('교환 신청에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setIsSubmitting(false);
    }
  }, [coupon, phoneNumber, isSelectable, selectedChoiceUnit]);

  // 로딩 중
  if (status === 'loading' || status === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  // 쿠폰 없음
  if (!coupon) {
    return (
      <div className="center-box min-h-screen flex-col gap-4 px-4 text-center">
        <ExclamationCircleIcon className="h-16 w-16 text-gray-300" />
        <p className="text-gray-500">쿠폰을 찾을 수 없습니다.</p>
        <p className="text-sm text-gray-400">
          유효하지 않은 쿠폰 코드이거나 이미 사용된 쿠폰입니다.
        </p>
      </div>
    );
  }

  // 교환 상태 메시지
  const statusMessage = getRedeemStatusMessage(coupon.redeemStatus);

  // 교환 신청 성공 화면
  if (submitSuccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-stone-100 to-white px-4">
        <div className="w-full max-w-md text-center">
          <CheckCircleSolidIcon className="mx-auto h-16 w-16 text-stone-600" />
          <h2 className="mt-4 text-xl font-medium text-stone-800">
            신청이 완료되었습니다
          </h2>
          <p className="mt-2 text-stone-500">입력하신 번호로 곧 전송됩니다</p>
          <p className="mt-3 break-keep text-sm leading-relaxed text-stone-400">
            영업일 기준 10시, 14시, 18시에 발송됩니다.
            <br />
            발송까지 30분~1시간 소요될 수 있습니다.
            <br />
            (주말 및 공휴일 제외, 익영업일 기준)
          </p>

          {/* 상품 정보 */}
          {(() => {
            // 선택형 상품인 경우
            if (selectedChoiceUnit) {
              const selectedUnit = choiceUnits.find((u) => u._id === selectedChoiceUnit);
              if (!selectedUnit) return null;
              return (
                <div className="mt-6">
                  {selectedUnit.image?.url && (
                    <img
                      src={fileBucketUrl(selectedUnit.image.url, 'w400')}
                      alt={selectedUnit.name}
                      className="mx-auto h-32 w-32 rounded-xl object-cover shadow-md"
                    />
                  )}
                  <p className="mt-3 text-lg font-medium text-stone-700">
                    {selectedUnit.name}
                  </p>
                  <p className="mt-1 text-xs text-stone-300">{couponCode}</p>
                </div>
              );
            }
            // 일반 상품인 경우
            return (
              <div className="mt-6">
                {imageUrl && (
                  <img
                    src={fileBucketUrl(imageUrl, 'w400')}
                    alt={itemName || '답례품'}
                    className="mx-auto h-32 w-32 rounded-xl object-cover shadow-md"
                  />
                )}
                <p className="mt-3 text-lg font-medium text-stone-700">{itemName}</p>
                <p className="mt-1 text-xs text-stone-500">{couponCode}</p>
              </div>
            );
          })()}

          <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6">
            <p className="text-sm text-stone-400">수신 번호</p>
            <p className="mt-2 text-2xl font-medium tracking-wide text-stone-700">
              {phoneNumber}
            </p>
          </div>

          <div className="mt-6 text-center">
            <a
              href="mailto:smartnanumi@kakao.com"
              className="inline-flex items-center gap-1 text-xs text-stone-300 transition hover:text-stone-400"
            >
              <MailIcon className="h-3 w-3" />
              <span>문의 smartnanumi@kakao.com</span>
            </a>
          </div>
        </div>
      </div>
    );
  }

  // 이미 처리된 쿠폰 (교환 불가)
  if (statusMessage) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-100 to-white px-4 py-8">
        <div className="mx-auto max-w-md">
          {/* 상태 메시지 */}
          <div
            className={`rounded-xl p-6 text-center ${
              statusMessage.type === 'success'
                ? 'border border-stone-200 bg-stone-50'
                : statusMessage.type === 'info'
                ? 'border border-stone-200 bg-stone-50'
                : 'border border-red-100 bg-red-50'
            }`}
          >
            {statusMessage.type === 'success' ? (
              <CheckCircleSolidIcon className="mx-auto h-10 w-10 text-stone-600" />
            ) : statusMessage.type === 'info' ? (
              <CheckCircleIcon className="mx-auto h-10 w-10 text-stone-500" />
            ) : (
              <ExclamationCircleIcon className="mx-auto h-10 w-10 text-red-400" />
            )}
            <h2 className="mt-3 text-lg font-medium text-stone-800">
              {statusMessage.title}
            </h2>
            <p className="mt-1 whitespace-pre-line text-sm text-stone-500">
              {statusMessage.message}
            </p>
          </div>

          {/* 상품 정보 */}
          {(() => {
            // 선택한 choiceUnit이 있는 경우
            const selectedUnit =
              coupon.selectedChoiceUnit && typeof coupon.selectedChoiceUnit === 'object'
                ? coupon.selectedChoiceUnit
                : null;

            if (selectedUnit) {
              return (
                <div className="mt-6 overflow-hidden rounded-xl border border-stone-200 bg-white">
                  <div className="aspect-square w-full overflow-hidden bg-stone-100">
                    {selectedUnit.image?.url ? (
                      <img
                        src={fileBucketUrl(selectedUnit.image.url, 'w400')}
                        alt={selectedUnit.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-stone-400">
                        No Image
                      </div>
                    )}
                  </div>
                  <div className="relative p-4 text-center">
                    <div className="flex items-center justify-center">
                      <span className="inline-block rounded-full bg-stone-100 px-3 py-1 text-sm font-medium text-stone-600">
                        선택 상품
                      </span>
                      <span className="absolute right-4 text-xs text-stone-500">
                        {couponCode}
                      </span>
                    </div>
                    <h3 className="mt-2 text-lg font-medium text-stone-800">
                      {selectedUnit.name}
                    </h3>
                  </div>
                </div>
              );
            }

            // 일반 상품인 경우
            return (
              <div className="mt-6 overflow-hidden rounded-xl border border-stone-200 bg-white">
                <div className="aspect-[4/3] w-full overflow-hidden bg-stone-100">
                  {imageUrl ? (
                    <img
                      src={fileBucketUrl(imageUrl, 'w400')}
                      alt={itemName || '답례품'}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-stone-400">
                      No Image
                    </div>
                  )}
                </div>
                <div className="p-4">
                  {brandName && (
                    <span className="inline-block rounded-full border border-gray-400 bg-white px-2 py-0.5 text-xs font-medium text-stone-600">
                      {brandName}
                    </span>
                  )}
                  <div className="mt-1 flex items-baseline justify-between gap-2">
                    <h3 className="text-lg font-medium text-stone-800">{itemName}</h3>
                    <span className="flex-shrink-0 text-xs text-stone-500">
                      {couponCode}
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}

          {!statusMessage.hideContact && (
            <div className="mt-6 text-center">
              <a
                href="mailto:smartnanumi@kakao.com"
                className="inline-flex items-center gap-1 text-xs text-stone-300 transition hover:text-stone-400"
              >
                <MailIcon className="h-3 w-3" />
                <span>문의 smartnanumi@kakao.com</span>
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  const isMainSlide = carouselIndex === 0;
  const displayBrandName = isMainSlide ? brandName : null;

  // 교환 신청 폼
  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-100 to-white pb-8">
      {/* 메인 이미지 캐로셀 + 감사 문구 오버레이 */}
      <div className="relative">
        <div
          className="aspect-square w-full overflow-hidden bg-stone-200"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div
            className="flex h-full transition-transform duration-300 ease-out"
            style={{ transform: `translateX(-${carouselIndex * 100}%)` }}
          >
            {carouselImages.map((ci, idx) => (
              <div key={idx} className="relative h-full w-full flex-shrink-0">
                {ci.url ? (
                  <img
                    src={fileBucketUrl(ci.url, 'w400')}
                    alt={ci.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-stone-400">
                    No Image
                  </div>
                )}
                {ci.unitId && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-4 pb-4 pt-8">
                    {ci.brandName && (
                      <span className="mb-1 inline-block rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium text-white backdrop-blur-sm">
                        {ci.brandName}
                      </span>
                    )}
                    <p className="text-base font-bold text-white">{ci.name}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 감사 문구 오버레이 (이미지 상단) */}
        <div className="pointer-events-none absolute left-0 right-0 top-0 bg-gradient-to-b from-black/50 to-transparent px-4 pb-8 pt-3 text-center">
          <p className="text-sm font-bold text-white/90">따뜻한 마음에 감사드립니다.</p>
        </div>

        {/* 브랜드 뱃지 */}
        {displayBrandName && (
          <div className="absolute bottom-14 left-4 rounded-full border border-gray-400 bg-white/90 px-3 py-1 text-sm font-medium text-stone-700 shadow-sm">
            {displayBrandName}
          </div>
        )}

        {/* 캐로셀 네비게이션 */}
        {carouselImages.length > 1 && (
          <>
            {carouselIndex > 0 && (
              <button
                onClick={() => setCarouselIndex((prev) => prev - 1)}
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-1.5 text-white backdrop-blur-sm"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
            )}
            {carouselIndex < carouselImages.length - 1 && (
              <button
                onClick={() => setCarouselIndex((prev) => prev + 1)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-1.5 text-white backdrop-blur-sm"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            )}

            {/* 인디케이터 dots */}
            <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-1.5">
              {carouselImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCarouselIndex(idx)}
                  className={`h-2 rounded-full transition-all ${
                    idx === carouselIndex ? 'w-5 bg-white' : 'w-2 bg-white/50'
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="px-4 pt-5">
        {/* 상품명 */}
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-base font-medium text-stone-800">
            {selectedUnit?.name || itemName}
          </h2>
          <span className="flex-shrink-0 text-xs text-stone-500">{couponCode}</span>
        </div>
        {/* 유효기간 안내 */}
        <div className="mt-4 space-y-2">
          {coupon.expiresAt && (
            <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
              <div>
                <span className="text-sm font-medium text-amber-800">신청 기한</span>
                <p className="text-xs text-amber-600">이 링크로 신청 가능한 기한</p>
              </div>
              <span className="text-sm font-bold text-amber-800">
                ~
                {new Date(coupon.expiresAt).toLocaleDateString('ko-KR', {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                })}
              </span>
            </div>
          )}

          {couponDetail && (couponDetail.validPeriod || couponDetail.placeOfExchange) && (
            <div className="rounded-lg bg-stone-50 p-3">
              {couponDetail.validPeriod && (
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-bold text-stone-600">
                      상품 유효기간
                    </span>
                    <p className="text-xs font-medium text-stone-400">
                      발송된 쿠폰의 사용 기한
                    </p>
                  </div>
                  <span className="text-sm font-medium text-stone-700">
                    {couponDetail.validPeriod}
                  </span>
                </div>
              )}
              {couponDetail.placeOfExchange && (
                <div
                  className={`flex items-center justify-between gap-4 ${
                    couponDetail.validPeriod ? 'mt-2 border-t border-stone-200 pt-2' : ''
                  }`}
                >
                  <span className="whitespace-pre text-sm font-bold text-stone-500">
                    사용처
                  </span>
                  <span className="break-keep text-sm text-stone-600">
                    {couponDetail.placeOfExchange}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 선택형 상품 리스트 */}
        {isSelectable && choiceUnits.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-stone-800 px-2.5 py-0.5 text-base font-bold text-white">
                STEP 1
              </span>
              <h3 className="break-keep text-lg font-bold text-stone-700">
                원하시는 상품을 선택해 주세요
              </h3>
            </div>

            <div className="mt-3 space-y-2">
              {choiceUnits.map((unit) => (
                <button
                  key={unit._id}
                  onClick={() => handleSelectUnit(unit._id)}
                  className={`flex w-full items-center gap-3 rounded-lg border p-3 transition ${
                    selectedChoiceUnit === unit._id
                      ? 'border-stone-700 bg-stone-100'
                      : 'border-stone-200 bg-white'
                  }`}
                >
                  {unit.image?.url && (
                    <img
                      src={fileBucketUrl(unit.image.url, 'w200')}
                      alt={unit.name}
                      className="h-14 w-14 rounded object-cover"
                    />
                  )}
                  <div className="flex-1 text-left">
                    {unit.giftItemBrand?.name && (
                      <span className="mb-0.5 inline-block rounded-full border border-gray-400 bg-white px-2 py-0.5 text-xs font-medium text-stone-600">
                        {unit.giftItemBrand?.name}
                      </span>
                    )}
                    <p
                      className={`break-keep font-medium ${
                        selectedChoiceUnit === unit._id
                          ? 'text-stone-900'
                          : 'text-stone-700'
                      }`}
                    >
                      {unit.name}
                    </p>
                  </div>
                  {selectedChoiceUnit === unit._id && (
                    <CheckCircleSolidIcon className="h-7 w-7 text-stone-800" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 핸드폰 번호 입력 */}
        <div
          ref={phoneInputRef}
          className="mt-6 rounded-xl border border-stone-200 bg-white p-5"
        >
          {isSelectable && (
            <div className="mb-4 flex items-center justify-center gap-2">
              <span className="rounded-full bg-stone-800 px-2.5 py-0.5 text-base font-bold text-white">
                STEP 2
              </span>
            </div>
          )}

          <h3 className="text-center text-lg font-bold text-stone-700">
            받으실 번호를 입력해 주세요
          </h3>

          <div className="mt-4">
            <div className="relative">
              <PhoneIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-300" />
              <input
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder="010-1234-5678"
                maxLength={13}
                className="w-full rounded-lg border border-stone-200 py-4 pl-12 pr-4 text-center text-xl font-medium tracking-wider text-stone-700 focus:border-stone-400 focus:outline-none"
              />
            </div>
            <p className="mt-2 text-center text-sm text-stone-400">
              입력하신 번호로 전송됩니다
            </p>
          </div>

          {submitError && (
            <p className="mt-3 text-center text-sm text-red-500">{submitError}</p>
          )}

          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !phoneNumber}
            className={`mt-5 flex w-full items-center justify-center rounded-lg py-4 font-medium transition ${
              isSubmitting || !phoneNumber
                ? 'bg-stone-200 text-stone-400'
                : 'bg-stone-800 text-white active:bg-stone-900'
            }`}
          >
            {isSubmitting ? '처리 중...' : '받기'}
          </button>
        </div>

        {/* 유의사항 */}
        <div className="mt-4 rounded-lg bg-stone-100 p-3">
          <p className="text-xs font-medium text-stone-500">유의사항</p>
          <ul className="mt-1.5 space-y-1 text-xs text-stone-400">
            <li>
              • 교환권 신청 기한 및 상품 유효기간 내 미사용으로 인한 소멸은 본인에게
              책임이 있습니다.
            </li>
            <li>• 발송된 쿠폰은 해당 브랜드의 정책에 따라 사용 가능합니다.</li>
            <li>• 잘못된 번호 입력으로 인한 발송 오류는 재발송이 불가합니다.</li>
          </ul>
        </div>

        {/* 이용문의 */}
        <div className="mt-4 text-center">
          <a
            href="mailto:smartnanumi@kakao.com"
            className="inline-flex items-center gap-1 text-xs text-stone-300 transition hover:text-stone-400"
          >
            <MailIcon className="h-3 w-3" />
            <span>문의 smartnanumi@kakao.com</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default GiftCouponRedeemPage;
